import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

// Page title row. `back` = { to, label } renders a small back link above the title.
export function PageHeader({ title, subtitle, actions, back }) {
  return (
    <div className="page-header">
      <div>
        {back && (
          <Link to={back.to} className="row muted" style={{ gap: 6, fontSize: 13, marginBottom: 8 }}>
            <ArrowLeft size={14} aria-hidden="true" />
            {back.label}
          </Link>
        )}
        <h1 className="page-title">{title}</h1>
        {subtitle && <p className="page-subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}

const STATUS_LABELS = {
  pending: 'Pending',
  picking: 'Picking',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export function StatusBadge({ status }) {
  return <span className={`badge badge-${status}`}>{STATUS_LABELS[status] || status}</span>;
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="empty-state">
      {Icon && (
        <div className="empty-state-icon">
          <Icon size={26} aria-hidden="true" />
        </div>
      )}
      <h3>{title}</h3>
      {description && <p>{description}</p>}
      {action}
    </div>
  );
}

export function LoadingState({ label = 'Loading…' }) {
  return <div className="loading" role="status">{label}</div>;
}

// Label + control + hint/error. Pass the control as children with a matching id.
export function Field({ label, htmlFor, hint, error, children }) {
  return (
    <div className="input-group">
      {label && <label htmlFor={htmlFor}>{label}</label>}
      {children}
      {error ? <span className="field-error">{error}</span> : hint && <span className="field-hint">{hint}</span>}
    </div>
  );
}

// Filter tabs: options = [{ value, label, count? }]
export function SegmentedControl({ options, value, onChange, label }) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map(option => (
        <button
          key={option.value}
          type="button"
          className={`segmented-option ${value === option.value ? 'active' : ''}`}
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
          {option.count !== undefined && <span className="segmented-count">{option.count}</span>}
        </button>
      ))}
    </div>
  );
}

// Callout box. type = error | success | warning | info
export function Callout({ type = 'info', icon: Icon, children }) {
  return (
    <div className={`message message-${type}`} role={type === 'error' ? 'alert' : undefined}>
      {Icon && <Icon size={18} aria-hidden="true" />}
      <div>{children}</div>
    </div>
  );
}

export function Avatar({ email }) {
  return <span className="avatar" aria-hidden="true">{(email || '?').charAt(0).toUpperCase()}</span>;
}

// Brand mark: a sticker with a highlighter-yellow peeled corner
// The pickBlink mark: storage as a grid of spots, one of them lit (brand/README.md)
const LOGO_SPOTS = [[4, 4], [24, 4], [44, 4], [4, 24], [24, 24], [4, 44], [24, 44], [44, 44]];

export function Logo({ size = 28, onDark = false }) {
  const spot = onDark ? '#3f3f46' : '#dedbd3';
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      {LOGO_SPOTS.map(([x, y]) => <rect key={`${x}-${y}`} x={x} y={y} width="16" height="16" rx="4" fill={spot} />)}
      <rect x="44" y="24" width="16" height="16" rx="4" fill="#ffe14d" />
      <rect x="42.25" y="22.25" width="19.5" height="19.5" rx="5.5" fill="none" stroke="#ff5b1f" strokeWidth="2.5" />
    </svg>
  );
}

// "pickBlink" (lowercase p, capital B), with "Blink" in the accent colour as in the logo
export function Wordmark() {
  return <span>pick<span className="brand-accent">Blink</span></span>;
}
